import { useState } from "react";

import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";

import forkIcon from "@assets/fork-icon.svg";
import { GenericTooltip } from "@components/tooltip-container";
import { type GetLabelWizardDataV3_me_labelFiltersV3 } from "@north-beam/nb-client-side";
import {
  type BreakdownGroupDescriptor,
  type SegmentLevel,
  type SegmentOperator,
  createEmptyBreakdownGroup,
} from "@north-beam/nb-common";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";

export type PopularBreakdownOption =
  | "PlatformNorthbeam"
  | "CategoryNorthbeam"
  | "TargetingNorthbeam"
  | null;

interface Props {
  showConfirmation: boolean;
  selected: PopularBreakdownOption | null;
  labelFilters: GetLabelWizardDataV3_me_labelFiltersV3[];
  onSelect: (
    name: PopularBreakdownOption,
    breakdown: BreakdownGroupDescriptor,
  ) => void;
}

function createPlatformNorthbeam(
  labelFilters: GetLabelWizardDataV3_me_labelFiltersV3[],
): BreakdownGroupDescriptor {
  const name = "Platform (Northbeam) Copy";
  const platformNorthbeamFilters = {
    nameBeforeEdit: name,
    name,
    segments: labelFilters
      .filter((labelFilter) => labelFilter.labelKey === "Platform (Northbeam)")
      .map((pfnbFilter) => {
        return {
          id: uuidv4(),
          name: pfnbFilter.labelValue,
          active: true,
          level: "campaign" as SegmentLevel,
          operator: "and" as SegmentOperator,
          matchers: [],
          filters: [[pfnbFilter.id]],
          error: null,
        };
      }),
    error: null,
  };
  return platformNorthbeamFilters;
}

function createCategoryNorthbeam(
  labelFilters: GetLabelWizardDataV3_me_labelFiltersV3[],
): BreakdownGroupDescriptor {
  const name = "Category (Northbeam) Copy";
  const categoryNorthbeamFilters = {
    nameBeforeEdit: name,
    name,
    segments: labelFilters
      .filter((labelFilter) => labelFilter.labelKey === "Category (Northbeam)")
      .map((cnbFilter) => {
        return {
          id: uuidv4(),
          name: cnbFilter.labelValue,
          active: true,
          level: "campaign" as SegmentLevel,
          operator: "and" as SegmentOperator,
          matchers: [],
          filters: [[cnbFilter.id]],
          error: null,
        };
      }),
    error: null,
  };
  return categoryNorthbeamFilters;
}

function createTargetingNorthbeam(
  labelFilters: GetLabelWizardDataV3_me_labelFiltersV3[],
): BreakdownGroupDescriptor {
  const name = "Targeting (Northbeam) Copy";
  const targetingNorthbeamFilters = {
    nameBeforeEdit: name,
    name,
    segments: labelFilters
      .filter((labelFilter) => labelFilter.labelKey === "Targeting (Northbeam)")
      .map((tnbFilter) => {
        return {
          id: uuidv4(),
          name: tnbFilter.labelValue,
          active: true,
          level: "campaign" as SegmentLevel,
          operator: "and" as SegmentOperator,
          matchers: [],
          filters: [[tnbFilter.id]],
          error: null,
        };
      }),
    error: null,
  };
  return targetingNorthbeamFilters;
}

export const DefaultBreakdowns = ({
  showConfirmation,
  selected,
  onSelect,
  labelFilters,
}: Props) => {
  const [aboutToSelect, setAboutToSelect] =
    useState<PopularBreakdownOption | null>(null);

  const selectDefaultBreakdown = (name: PopularBreakdownOption) => {
    if (name === selected) {
      onSelect(null, createEmptyBreakdownGroup());
      return;
    }
    if (name === "PlatformNorthbeam") {
      onSelect(name, createPlatformNorthbeam(labelFilters));
    }

    if (name === "CategoryNorthbeam") {
      onSelect(name, createCategoryNorthbeam(labelFilters));
    }

    if (name === "TargetingNorthbeam") {
      onSelect(name, createTargetingNorthbeam(labelFilters));
    }
  };

  return (
    <div>
      <div className="text-xs text-cosmic font-bold uppercase mb-3">
        Northbeam Breakdowns (click to prefill)
      </div>
      {aboutToSelect ? (
        <div
          className={clsx(
            "flex items-center justify-between gap-4 h-[62px] rounded bg-mars-2 px-3",
          )}
        >
          <div>
            <div className="text-sm font-bold mb-1">
              Show the "{aboutToSelect}" example breakdown?
            </div>
            <div className="text-xs">
              The edits you’ve made below will be lost
            </div>
          </div>
          <div className="flex gap-3">
            <PrimaryButton
              onClick={() => {
                selectDefaultBreakdown(aboutToSelect);
                setAboutToSelect(null);
              }}
              className="bg-mars hover:bg-mars+2"
            >
              YES, CONTINUE AND OVERWRITE
            </PrimaryButton>
            <SecondaryButton
              className="font-bold"
              onClick={() => setAboutToSelect(null)}
            >
              CANCEL
            </SecondaryButton>
          </div>
        </div>
      ) : (
        <div className="flex gap-4 h-[62px]">
          <GenericTooltip
            content="Click to create your own copy of Platform (Northbeam)"
            noInfoCircle
            className="flex-1 flex"
          >
            <button
              className={clsx(
                "flex-1 flex items-center justify-center gap-2 rounded bg-cosmic-4",
                "border hover:border-cosmic hover:bg-cosmic-3",
                {
                  "border-transparent": "PlatformNorthbeam" !== selected,
                  "border-cosmic": "PlatformNorthbeam" === selected,
                },
              )}
              onClick={() => {
                if (showConfirmation) {
                  setAboutToSelect("PlatformNorthbeam");
                } else {
                  selectDefaultBreakdown("PlatformNorthbeam");
                }
              }}
            >
              <i className="fa-solid fa-cube shrink-0 text-cosmic text-xs mr-0.5" />
              <div className="shrink-0 text-xs text-cosmic font-semibold uppercase">
                Platform
              </div>
              <div className="shrink-0 flex items-center gap-1">
                <img src={forkIcon} alt="Fork icon" />
                <div className="text-cosmic text-xxs text-left space-y-1">
                  <div>Facebook Ads</div>
                  <div>Google Ads</div>
                  <div>Tik Tok</div>
                </div>
              </div>
            </button>
          </GenericTooltip>
          <GenericTooltip
            content="Click to create your own copy of Category (Northbeam)"
            noInfoCircle
            className="flex-1 flex"
          >
            <button
              className={clsx(
                "flex-1 flex items-center justify-center gap-2 rounded bg-cosmic-4",
                "border hover:border-cosmic hover:bg-cosmic-3",
                {
                  "border-transparent": "CategoryNorthbeam" !== selected,
                  "border-cosmic": "CategoryNorthbeam" === selected,
                },
              )}
              onClick={() => {
                if (showConfirmation) {
                  setAboutToSelect("CategoryNorthbeam");
                } else {
                  selectDefaultBreakdown("CategoryNorthbeam");
                }
              }}
            >
              <i className="fa-solid fa-list shrink-0 text-cosmic text-xs mr-0.5" />
              <div className="shrink-0 text-xs text-cosmic font-semibold uppercase">
                Category
              </div>
              <div className="shrink-0 flex items-center gap-1">
                <img src={forkIcon} alt="Fork icon" />
                <div className="text-cosmic text-xxs text-left space-y-1">
                  <div>Smart Shopping</div>
                  <div>Affiliate</div>
                  <div>Organic</div>
                </div>
              </div>
            </button>
          </GenericTooltip>
          <GenericTooltip
            content="Click to create your own copy of Targeting (Northbeam)"
            noInfoCircle
            className="flex-1 flex"
          >
            <button
              className={clsx(
                "flex-1 flex items-center justify-center gap-2 rounded bg-cosmic-4",
                "border hover:border-cosmic hover:bg-cosmic-3",
                {
                  "border-transparent": "TargetingNorthbeam" !== selected,
                  "border-cosmic": "TargetingNorthbeam" === selected,
                },
              )}
              onClick={() => {
                if (showConfirmation) {
                  setAboutToSelect("TargetingNorthbeam");
                } else {
                  selectDefaultBreakdown("TargetingNorthbeam");
                }
              }}
            >
              <i className="fa-solid fa-bullseye shrink-0 text-cosmic text-xs mr-0.5" />
              <div className="shrink-0 text-xs text-cosmic font-semibold uppercase">
                Targeting
              </div>
              <div className="shrink-0 flex items-center gap-1">
                <img src={forkIcon} alt="Fork icon" />
                <div className="text-cosmic text-xxs text-left space-y-1">
                  <div>Paid - Prospecting</div>
                  <div>Paid - Retargeting</div>
                  <div>Paid - Branded Search</div>
                </div>
              </div>
            </button>
          </GenericTooltip>
        </div>
      )}
    </div>
  );
};
